import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@/ui';

import { REFLECTION_AI_HOMEPAGES } from './reflectionPrompt';

export interface ReflectionPromptReviewProps {
  draft: string;
  enabled: boolean;
  open: boolean;
  onChange: (draft: string) => void;
  onDiscard: () => void;
  canResume?: boolean;
  onResume?: () => void;
}

// Controlled, owner-memory draft. The parent captures source/generation at
// opening and purges it at an auth boundary; flag fetches never rebase it.
export const ReflectionPromptReview = ({
  draft,
  enabled,
  onChange,
  onDiscard,
  open,
  canResume,
  onResume,
}: ReflectionPromptReviewProps) => {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>(
    'idle',
  );
  const [manualHomepage, setManualHomepage] = useState<string | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  useEffect(() => {
    setCopyState('idle');
    setManualHomepage(null);
    setConfirmingDiscard(false);
  }, [open]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  };
  const discard = () => {
    if (draft) {
      setConfirmingDiscard(true);
    } else {
      onDiscard();
    }
  };
  return (
    <Dialog
      aria-labelledby="reflection-prompt-title"
      className="fs-mask"
      data-dd-privacy="mask"
      open={open}
      fullWidth
      onClose={discard}
    >
      <DialogTitle id="reflection-prompt-title">
        Review your AI prompt
      </DialogTitle>
      <DialogContent>
        <p>
          Review or edit exactly what you will copy. Nothing is sent to an AI
          provider.
        </p>
        {!enabled ? (
          <Alert severity="info">
            Reflection actions are unavailable. This draft is suspended; you can
            discard it or copy your own draft.
          </Alert>
        ) : null}
        <TextField
          disabled={!enabled}
          inputProps={{ 'aria-label': 'AI prompt', style: { fontSize: 16 } }}
          minRows={7}
          value={draft}
          fullWidth
          multiline
          onChange={(event) => {
            onChange(event.target.value);
            setCopyState('idle');
          }}
        />
        {copyState === 'copied' ? (
          <p role="status">Copied. Nothing was sent.</p>
        ) : null}
        {copyState === 'error' ? (
          <Alert severity="error">
            Couldn’t copy. Select and copy your text manually.
          </Alert>
        ) : null}
        {manualHomepage && enabled ? (
          <p>
            A tab may have opened. If not,{' '}
            <a href={manualHomepage} rel="noopener noreferrer" target="_blank">
              open the homepage manually
            </a>
            .
          </p>
        ) : null}
      </DialogContent>
      {confirmingDiscard ? (
        <div aria-label="Discard prompt draft" role="alertdialog">
          <p>Discard this prompt draft?</p>
          <Button onClick={() => setConfirmingDiscard(false)}>
            Keep editing
          </Button>
          <Button
            onClick={() => {
              setConfirmingDiscard(false);
              onDiscard();
            }}
          >
            Discard prompt draft
          </Button>
        </div>
      ) : null}
      <DialogActions style={{ flexWrap: 'wrap', gap: 8 }}>
        {canResume ? (
          <Button onClick={onResume}>Resume prompt draft</Button>
        ) : null}
        <Button onClick={discard}>Discard</Button>
        <Button onClick={copy}>
          {enabled ? 'Copy prompt' : 'Copy own draft'}
        </Button>
        {Object.entries(REFLECTION_AI_HOMEPAGES).map(([label, url]) => (
          <Button
            key={label}
            disabled={!enabled}
            onClick={() => {
              try {
                const tab = window.open(url, '_blank', 'noopener,noreferrer');
                // noopener can itself yield null: do not falsely claim success
                // or a blocked popup. Keep an explicit fixed-homepage fallback.
                setManualHomepage(tab ? null : url);
              } catch {
                setManualHomepage(url);
              }
            }}
          >
            Open {label}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
};
